import to from "await-to-js";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { Input } from "../../../../components/Input/Input";
import { Select } from "../../../../components/Select/Select";
import { Table } from "../../../../components/Table/Table";
import { ModalsContext } from "../../../../context/modals.context";
import { CentralRegistryContext } from "../../../../modules/ministry/centralRegistry/centralRegistry.context";
import { centralRegistryService } from "../../../../modules/ministry/centralRegistry/centralRegistry.service";
import { RegistarMaloprodajnihCenaPayload } from "../../../../modules/ministry/centralRegistry/centralRegistry.types";
import { RecordsContext } from "../../../../modules/obligee/records/records.context";
import { recordsService } from "../../../../modules/obligee/records/records.service";
import { mapSelectOptions } from "../../../../utils/map4select";
import style from "../CentralRegistry.module.scss";

const INITIAL_PARAMS = () => ({
  sifraDuvanskogProizvoda: null,
  robnaMarkaId: null,
});

export const RetailPriceRegistry = () => {
  const { centralRegistry, setCentralRegistry } = useContext(
    CentralRegistryContext
  );
  const { records, setRecords } = useContext(RecordsContext);
  const [params, setParams] = useState(INITIAL_PARAMS());
  const { setError } = useContext(ModalsContext);

  const postRegistarMaloprodajnihCena = useDebouncedCallback(async () => {
    const [err, res] = await to(
      centralRegistryService.postRegistarMaloprodajnihCena(params)
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setCentralRegistry({ retailTable: res.data });
  }, 400);

  const createRobneMarkePretraga = async (pretraga: string | null) => {
    const [err, res] = await to(
      recordsService.getAllTrademarks({ naziv: pretraga })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setRecords({ robneMarkePretraga: res.data });
  };
  const updateParams = (
    accessor: keyof RegistarMaloprodajnihCenaPayload,
    value: string | number | null
  ) => {
    setParams({ ...params, [accessor]: value });
  };

  useEffect(() => {
    postRegistarMaloprodajnihCena();
    createRobneMarkePretraga(null);
  }, [params]);

  const colDef = [
    {
      title: t("QR_CODE.VRSTA_DUVANSKOG_PROIZVODA"),
      dataIndex: "vrstaDuvanskogProizvoda",
      width: "160px",
    },
    {
      title: t("TABLES.NAZIV_ROBNE_MARKE"),
      dataIndex: "nazivMarkeDuvanskogProizvoda",
      width: "160px",
      render: (nazivMarkeDuvanskogProizvoda: string) => (
        <span style={{ textTransform: "none" }}>
          {nazivMarkeDuvanskogProizvoda}
        </span>
      ),
    },
    {
      title: t("TABLES.SIFRA_ROBNE_MARKE"),
      dataIndex: "sifraRobneMarke",
      width: "160px",
    },
    {
      title: t("TABLES.JEDINICA_MERE"),
      dataIndex: "jedinicaMere",
      width: "130px",
    },
    {
      title: t("TABLES.VELICINA_PAKOVANJA"),
      dataIndex: "velicinaPakovanja",
      width: "120px",
    },
    {
      title: t("TABLES.BROJ_SLUZBENOG_GLASNIKA_RS"),
      dataIndex: "brojSluzbenogGlasnika",
      width: "160px",
    },
    {
      title: t("TABLES.DATUM_OBJAVE_MPC"),
      dataIndex: "datumObjaveMPC",
      width: "160px",
    },
    {
      title: t("TABLES.IZNOS_MALOPRODAJNE_CENE"),
      dataIndex: "iznosMaloprodajneCene",
      width: "160px",
    },
    {
      title: t("TABLES.DATUM_OD_CENA"),
      dataIndex: "datumVazenjaMaloprodajneCene",
      width: "160px",
    },
  ];

  return (
    <div className={style.mainContainer}>
      <div id="retail_price_registry">
        <h2>{t("NAVIGATION.MALOPRODAJNE_CENE")}</h2>
        <div className={`${style.buttonContainer} w-40`}>
          <Input
            name="sifraRobneMarke"
            allowClear
            className="w-49"
            onChange={({ target }) =>
              updateParams("sifraDuvanskogProizvoda", target.value)
            }
            placeholder={t("TABLES.SIFRA_ROBNE_MARKE")}
          />
          <Select
            style={{ width: "50%" }}
            options={
              mapSelectOptions({
                sourceArray: records.robneMarkePretraga,
                valueAccessor: "id",
                labelAccessor: "naziv",
              }) || []
            }
            allowClear
            placeholder={t("PLACEHOLDERS.IZABERI_BREND")}
            onChange={(value) => updateParams("robnaMarkaId", value)}
            filterOption={(input, option) => {
              const label: any = option?.label;
              if (label) {
                return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }
              return [] as any;
            }}
            showSearch
            name="brend"
          />
        </div>
      </div>
      <Table
        columns={colDef}
        scrollConfig={["#retail_price_registry"]}
        dataSource={centralRegistry.retailTable}
        name="retail-price-registry-table"
      />
    </div>
  );
};
